/**
 * Dashboard Page
 *
 * Main dashboard overview with KPIs, charts, activity feed, and quick actions
 */

import { useGetCurrentUser } from '@/hooks/app';
import { REGION_ALL } from '@/utils/constant/appConstant';
import { PermissionChecker } from '@/utils/permission';
import type { MenuProps } from 'antd';
import { Button, Dropdown, Space } from 'antd';
import React, { useState } from 'react';
import { IoChevronDown } from 'react-icons/io5';
import { MdDateRange } from 'react-icons/md';
import {
  DashboardFollowUpTable,
  DashboardStats,
  QuoteStatusDistribution,
  QuotesOverTime
} from '../components';
import { DateRangeType } from '../types';

export const DashboardPage: React.FC = () => {
  // State for date range filter
  const [dateRange, setDateRange] = useState<DateRangeType>('thisweek');

  // Get current user permissions
  const { data: currentUserData } = useGetCurrentUser();
  const permissions = currentUserData?.permission;

  // Initialize PermissionChecker
  const permissionChecker = new PermissionChecker(permissions);

  // Permission checks
  const canViewAnalytics = permissionChecker.canViewDashboardAnalytics();
  const canViewFollowup = permissionChecker.canViewFollowup();
  const canManageFollowup = permissionChecker.canManageFollowup();

  // Region filter for follow-ups and stats
  const userRegion = currentUserData?.currentUser?.dogma_region;
  const isRegionAll = Boolean(userRegion && userRegion === REGION_ALL);
  const regionFilter = !isRegionAll && userRegion ? `dogma_Quote/dogma_region eq ${userRegion}` : undefined;
  const region = !isRegionAll && userRegion ? userRegion : undefined;

  // Date range menu items
  const dateRangeMenuItems: MenuProps['items'] = [
    {
      key: 'today',
      label: 'Today',
      onClick: () => setDateRange('today'),
    },
    {
      key: 'thisweek',
      label: 'This Week',
      onClick: () => setDateRange('thisweek'),
    },
    {
      key: 'thismonth',
      label: 'This Month',
      onClick: () => setDateRange('thismonth'),
    },
    {
      key: 'thisquarter',
      label: 'This Quarter',
      onClick: () => setDateRange('thisquarter'),
    },
    {
      key: 'thisyear',
      label: 'This Year',
      onClick: () => setDateRange('thisyear'),
    },
  ];

  // Get current date range label
  const getDateRangeLabel = () => {
    switch (dateRange) {
      case 'today':
        return 'Today';
      case 'thisweek':
        return 'This Week';
      case 'thismonth':
        return 'This Month';
      case 'thisquarter':
        return 'This Quarter';
      case 'thisyear':
        return 'This Year';
      default:
        return 'This Week';
    }
  };





  return (
    <>
      {/* Fixed Header with Search and Actions */}
      <div className="">
        <div className="flex h-full px-4 pt-4 items-center justify-between gap-x-4">
          <div>
            <h1 className='text-xl font-semibold'>Dashboard</h1>
            <p className='text-sm text-text-secondary'>Monitor your business performance and key metrics at a glance.</p>
          </div>
          <Space size="middle">
            {canViewAnalytics && (
              <Dropdown menu={{ items: dateRangeMenuItems, selectedKeys: [dateRange] }} trigger={['click']}>
                <Button
                  type="default"
                  icon={<MdDateRange size={18} />}
                >
                  {getDateRangeLabel()}
                  <IoChevronDown size={16} className="ml-1" />
                </Button>
              </Dropdown>
            )}
          </Space>
        </div>
      </div>

      {/* Stats Cards Section - Requires view_dashboard_analytics permission */}
      {canViewAnalytics && (
        <div className="px-4 pt-6">
          <DashboardStats dateRange={dateRange} region={region} />
        </div>
      )}

      {/* Charts Section - Requires view_dashboard_analytics permission */}
      {canViewAnalytics && (
        <div className="p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <QuoteStatusDistribution dateRange={dateRange} region={region} />
            <QuotesOverTime region={region} />
          </div>
        </div>
      )}

      {/* Follow-Up Queue Section - Requires view_followup permission */}
      {canViewFollowup && (
        <div className="px-4 pb-4">
          <DashboardFollowUpTable
            showRegion
            canManageFollowup={canManageFollowup}
            regionFilter={regionFilter}
          />
        </div>
      )}
    </>
  );
};

export default DashboardPage;
